"use client";

import { useAuth } from "@/hooks/use-auth";
import { isValidEmail } from "@/utils/verification";
import React, { useEffect, useRef, useState } from "react";
import { ShortEmailVerification } from "./email-verification-short";

// Email verification types
export type EmailVerificationStep = "email" | "otp" | "verified";
export const isValidOtp = (otp: string): boolean => {
  return /^\d{5}$/.test(otp);
};
interface EmailVerificationDialogProps {
  onVerificationSuccess?: (email: string, otp: string) => void;
  isShort?: boolean;
  className?: string;
  defaultEmail?: string;
  defaultStep?: EmailVerificationStep;
  required?: boolean;
}
export function EmailVerificationDialog({
  onVerificationSuccess,
  isShort = false,
  className,
  defaultEmail = "",
  defaultStep = "email",
  required = false
}: EmailVerificationDialogProps) {
  const [step, setStep] = useState<EmailVerificationStep>(defaultStep);
  const [email, setEmail] = useState(defaultEmail);
  const [otp, setOtp] = useState("");
  const {
    sendOtpMail,
    verifyOtpMail,
    user
  } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const resendIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Update email when defaultEmail prop changes
  useEffect(() => {
    setEmail(defaultEmail);
  }, [defaultEmail]);

  // Timer effect for resend functionality
  useEffect(() => {
    if (resendTimer > 0) {
      resendIntervalRef.current = setInterval(() => {
        setResendTimer(prev => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (resendIntervalRef.current) {
        clearInterval(resendIntervalRef.current);
        resendIntervalRef.current = null;
      }
    }
    return () => {
      if (resendIntervalRef.current) {
        clearInterval(resendIntervalRef.current);
      }
    };
  }, [resendTimer]);
  const handleSendOTP = async () => {
    if (!email || !isValidEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }
    setIsLoading(true);
    setError("");
    try {
      // Simulate API call for sending OTP to email
      await new Promise(resolve => setTimeout(resolve, 1500));
      const success = await sendOtpMail(email, user?.name ?? "");
      if (!success) {
        throw new Error("Failed to send OTP");
      }
      setStep("otp");
      setResendTimer(30);
      setCanResend(false);
    } catch (error_0) {
      setError("Failed to send OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };
  const handleResendOTP = async () => {
    if (!canResend) return;
    setIsLoading(true);
    setError("");
    try {
      // Simulate API call for resending OTP
      await new Promise(resolve_0 => setTimeout(resolve_0, 1500));
      const success_0 = await sendOtpMail(email, user?.name ?? "", true);
      if (!success_0) {
        throw new Error("Failed to resend OTP");
      }
      setResendTimer(30);
      setCanResend(false);
    } catch (error_1) {
      setError("Failed to resend OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };
  const handleVerifyOTP = async () => {
    if (!otp || !isValidOtp(otp)) {
      setError("Please enter a valid 5-digit OTP");
      return;
    }
    setIsLoading(true);
    setError("");
    try {
      // Simulate API call for verifying OTP
      await new Promise(resolve_1 => setTimeout(resolve_1, 1500));
      const success_1 = await verifyOtpMail(email, otp);
      if (!success_1) {
        throw new Error("Invalid OTP");
      }
      setStep("verified");
      onVerificationSuccess?.(email, otp);
    } catch (error_2) {
      setError("Invalid OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };
  const handleBackToEmail = () => {
    setStep("email");
    setOtp("");
    setError("");
  };
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      if (step === "email") {
        handleSendOTP();
      } else if (step === "otp") {
        handleVerifyOTP();
      }
    }
  };
  const formProps = {
    step,
    email,
    setEmail,
    otp,
    setOtp,
    error,
    isLoading,
    handleSendOTP,
    handleVerifyOTP,
    handleBackToEmail,
    handleResendOTP,
    resendTimer,
    canResend,
    handleKeyPress,
    required
  };

  // Short email verification form
  if (isShort) {
    return <ShortEmailVerification {...formProps} />;
  }
  return <div className={className}>
			<ShortEmailVerification {...formProps} />
		</div>;
}